import { Space, Spin } from 'antd';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { computeMediaErrors } from 'components/common/UploadModal/utils/mediaValidation';
import useMediaRules from 'hooks/useMediaRules';
import { closeUploadModal } from 'modules/mediaLibrary/actions';
import { getDefaultFetchParams, getMediaMode } from 'modules/mediaLibrary/selector';
import { uploadMedias } from 'modules/medias/actions';
import { isUploading } from 'modules/medias/selectors';
import Button from 'ui/base/Button';

import Content from './Content';
import { Modal, Loader, Mask } from './UI';
import { DISPLAY_TYPES, format, NUMBER_MAX_FILES } from './utils';
import useAsyncDispatch from '../../../hooks/useAsyncDispatch';

/**
 * UploadModal - A modal component for uploading and selecting media files
 *
 * This modal provides multiple ways to add media to your application:
 * - Upload new files from computer
 * - Take photos with camera (if enabled)
 * - Select from existing media library
 *
 * @typedef {Object} UploadModalProps
 * @prop {number} [limit=25] - Maximum number of files that can be uploaded in one session
 * @prop {Object} [params] - Additional parameters sent with upload requests (entityType, subType, etc.)
 * @prop {string} [linkURL] - URL endpoint for importing media from external URLs
 * @prop {string} [uploadURL] - URL endpoint for uploading new files
 * @prop {Object} [uploadData] - Additional data to include with upload requests
 * @prop {boolean} [temporary=false] - Whether uploads are temporary (will be cleaned up if not confirmed)
 * @prop {boolean} [autoBrowse=false] - Automatically open file browser when modal opens
 * @prop {boolean} [uploadInLibrary=false] - Whether uploaded files should be saved to media library
 * @prop {boolean} [persistAtUpload=false] - Persist media immediately on upload vs waiting for confirmation
 * @prop {string} [defaultTab='COMPUTER'] - Which tab to show by default (COMPUTER, CAMERA, LIBRARY, URL)
 * @prop {Array} [displayTypes] - Which upload methods to show as tabs
 * @prop {import("hooks/useMediaRules").AllowedMedias} [allowed] - File type restrictions with extensions and MIME types
 * @prop {Array} [medias=[]] - Pre-selected files to show in the modal
 * @prop {Function} [onUploaded] - Callback fired when upload completes successfully
 * @prop {string} [modalClassName] - Additional CSS classes for the modal container
 * @prop {boolean} [displayMaxSizeMessage]
 * @prop {{ types?: string[] }} [librariesFilter] - Filter media library items by type
 */

/** @type {React.FC<UploadModalProps & import("antd").ModalProps>} */
const UploadModal = ({
  linkURL,
  allowed,
  uploadURL,
  onUploaded,
  uploadData,
  medias = [],
  modalClassName,
  displayMaxSizeMessage,
  uploadInLibrary,
  persistAtUpload,
  temporary = false,
  autoBrowse = false,
  params: uploadParams,
  limit = NUMBER_MAX_FILES,
  defaultTab = DISPLAY_TYPES.COMPUTER,
  displayTypes = Object.values(DISPLAY_TYPES),
  ...modalProps
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const asyncDispatch = useAsyncDispatch();

  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const [isFullScreen, setIsFullScreen] = React.useState(false);
  const [selectedTabs, setSelectedTabs] = React.useState(defaultTab);
  const [selectedMedias, setSelectedMedias] = React.useState(medias.map(file => format(file, uploadInLibrary)));

  const mode = useSelector(getMediaMode);
  const isLoading = useSelector(isUploading);

  const { page: _page, size: _size, ...params } = useSelector(getDefaultFetchParams);
  const { config } = useMediaRules(allowed);

  const mediasErrors = React.useMemo(() => computeMediaErrors(selectedMedias, config), [config, selectedMedias]);

  const hasMedias = selectedMedias.length > 0;
  const disabledImport = !hasMedias || isLoading || selectedMedias.length > limit || Boolean(mediasErrors);
  const displayMask = isDrawerOpen || isLoading;
  const isRetry = selectedMedias.some(media => media.isUploaded === false);
  const displayRetryFooter = isRetry && !isDrawerOpen;
  const disableAddMedia = (isRetry && !displayMask) || selectedMedias.length >= limit;

  const handleOnClose = () => {
    dispatch(closeUploadModal());
    setSelectedTabs(defaultTab);
    if (isRetry) {
      const uploadedMedias = selectedMedias.filter(media => media.isUploaded);
      onUploaded?.({
        uploadedFiles: uploadedMedias.filter(media => !('media' in media)),
      });
    }
    setSelectedMedias([]);
    modalProps.onCancel?.();
  };

  const handleOnClick = async () => {
    try {
      const mediasToUpload = selectedMedias.filter(media => !media.isUploaded);
      const result = await asyncDispatch(
        uploadMedias(
          {
            params,
            linkURL,
            temporary,
            uploadData,
            uploadParams,
            persistAtUpload,
            baseUrl: uploadURL,
            medias: isRetry ? mediasToUpload : selectedMedias,
          },
          uploadInLibrary ? { source: `dashboard-${mode}` } : {}
        )
      );

      onUploaded?.({ uploadedFiles: result.uploadedMedias });
      setSelectedMedias([]);
      setSelectedTabs(defaultTab);
    } catch (error) {
      const mergedMedias = selectedMedias.map(media => {
        const failedMedia = [...error.uploadedMedias, ...error.linkedMediaLibraryItems].find(
          mediaFail => mediaFail.id === media.id || (mediaFail.isUploaded && mediaFail.idBeforeUpload === media.id)
        );
        if (failedMedia) {
          return failedMedia;
        }
        return media;
      });
      setSelectedMedias(mergedMedias);
    }
  };

  const handleOnFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  return (
    <Modal
      large
      open
      title={
        <div className="d-flex align-items-center justify-content-between">
          <FormattedMessage id="mediaLibrary.uploadMedia" defaultMessage="Upload Media" />
          <i
            onClick={handleOnFullScreen}
            className={classNames('cursor-pointer', { 'fb-icon--collapse': isFullScreen, 'fb-icon--expand': !isFullScreen })}
            title={intl.formatMessage(
              isFullScreen
                ? { id: 'mediaLibrary.restore', defaultMessage: 'Restore' }
                : { id: 'mediaLibrary.maximise', defaultMessage: 'Maximise' }
            )}
          />
        </div>
      }
      {...modalProps}
      className={classNames('scrollableModal', { 'full-screen': isFullScreen }, modalClassName)}
      onCancel={handleOnClose}
      footer={
        <>
          {displayRetryFooter && (
            <div className="d-flex w-100 justify-content-end">
              <Space>
                <Button onClick={handleOnClick}>
                  <FormattedMessage id="retryUpload" defaultMessage="Retry upload" />
                </Button>
                <Button onClick={handleOnClose} type="primary" data-cy="mediaLibrary-continue">
                  <FormattedMessage id="continue" defaultMessage="Continue" />
                </Button>
              </Space>
            </div>
          )}
          {!displayRetryFooter && (
            <div className="d-flex w-100 justify-content-between">
              <Button onClick={handleOnClose}>
                <FormattedMessage id="cancel" defaultMessage="Cancel" />
              </Button>
              <Button onClick={handleOnClick} type="primary" disabled={disabledImport} data-cy="mediaLibrary-import">
                <FormattedMessage id="import" defaultMessage="Import" />
              </Button>
            </div>
          )}
        </>
      }
    >
      {displayMask && (
        <Mask>
          {isLoading && (
            <div className="h-100 w-100 d-flex justify-content-center align-items-center">
              <Loader>
                <Spin indicator={<span className="fb-icon--spin fb-icon-spin" />} size="large" />
              </Loader>
            </div>
          )}
        </Mask>
      )}

      <Content
        limit={limit}
        tabs={selectedTabs}
        mediasErrors={mediasErrors}
        autoBrowse={autoBrowse}
        displayMaxSizeMessage={displayMaxSizeMessage}
        medias={selectedMedias}
        showDrawer={isDrawerOpen}
        displayTypes={displayTypes}
        onDrawerOpen={setDrawerOpen}
        onChangeTabs={setSelectedTabs}
        uploadInLibrary={uploadInLibrary}
        onChangeMedias={setSelectedMedias}
        disabledAddMedia={disableAddMedia}
        // allowed types and extensions
        allowed={allowed}
      />
    </Modal>
  );
};

export default UploadModal;
